import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import { useSendFunnelEnergyPlanPreview } from "@/api/hooks";
import { ApiError } from "@/api/client";
import PostcodeAddressLookup from "@/components/quote/PostcodeAddressLookup";
import { useQuoteFlow } from "@/contexts/QuoteFlowContext";
import { useAuthContactFields } from "@/hooks/useAuthContactFields";
import { useQuoteSession } from "@/hooks/useQuoteSession";
import { annualKwhFromMonthlyBill, savePendingPlanDraft } from "@/utils/planHandoff";
import { gbp } from "@/utils/phoenixFinanceExample";
import { composeAddress, type UkAddressParts } from "@/utils/quoteMapper";
import { formatStartingPointFacts } from "@/utils/startingPointFacts";
import { toast } from "sonner";
import EmailPlanConsent from "./EmailPlanConsent";
import PropertyAerial from "./PropertyAerial";
import ToggleRow from "./ToggleRow";

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

type Step = "postcode" | "confirm" | "details" | "result";

function formatSelectedAddress(fields: UkAddressParts): string {
  const line = composeAddress(fields);
  const postcode = fields.postcode.trim().toUpperCase();
  if (!postcode) return line;
  if (line.toUpperCase().includes(postcode)) return line;
  return [line, postcode].filter(Boolean).join(", ");
}

export default function QuoteFunnel({ compact = false, focusToken = 0 }: { compact?: boolean; focusToken?: number }) {
  const [step, setStep] = useState<Step>("postcode");
  const [address, setAddress] = useState<UkAddressParts | null>(null);
  const [startingPoint, setStartingPoint] = useState<string | null>(null);
  const [checkingHome, setCheckingHome] = useState(false);
  const factsRequestRef = useRef(0);
  const session = useQuoteSession();
  const [bill, setBill] = useState(150);
  const [ev, setEv] = useState(false);
  const [heatPump, setHeatPump] = useState(false);
  const { name, setName, email, setEmail, phone, setPhone } = useAuthContactFields();
  const [emailConsent, setEmailConsent] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [, navigate] = useLocation();
  const { setPendingAddress, setPendingEnrichment, setPendingPlanContact } = useQuoteFlow();
  const sendPreviewEmail = useSendFunnelEnergyPlanPreview();

  const cancelHomeFacts = useCallback(() => {
    factsRequestRef.current += 1;
    setStartingPoint(null);
    setCheckingHome(false);
  }, []);

  const loadHomeFacts = (fields: UkAddressParts) => {
    const requestId = ++factsRequestRef.current;
    setStartingPoint(null);
    setCheckingHome(true);

    void session.confirmAddress(fields, {
      lastStep: "postcode",
      data: { postcode: fields.postcode, address1: fields.address1 },
    }).then((enrichment) => {
      if (requestId !== factsRequestRef.current) return;
      if (enrichment) setPendingEnrichment(fields, enrichment);
      setCheckingHome(false);
      setStartingPoint(formatStartingPointFacts({ enrichment }));
    }).catch(() => {
      if (requestId !== factsRequestRef.current) return;
      setCheckingHome(false);
    });
  };

  useEffect(() => {
    if (!focusToken) return;
    cancelHomeFacts();
    setStep("postcode");
  }, [cancelHomeFacts, focusToken]);

  useEffect(() => {
    if (!focusToken || step !== "postcode") return;
    document.getElementById("funnel-pc")?.focus({ preventScroll: true });
  }, [focusToken, step]);

  const result = useMemo(() => {
    if (!address) return null;
    // Heat pumps roughly double winter electricity use — a bigger load than
    // an EV, so it weighs more heavily on both saving potential and the
    // recommended system size (illustrative figures, like the EV ones).
    const base = 1600 + bill * 6 + (ev ? 220 : 0) + (heatPump ? 260 : 0);
    const annualSaving = Math.round(base / 10) * 10;
    const monthlySaving = Math.round(annualSaving / 12);
    // Can't save more of the bill than there is — cap the monthly saving
    // shown at 85% of the current bill, so "new bill" never looks silly.
    const cappedMonthlySaving = Math.min(monthlySaving, Math.round(bill * 0.85));
    const newMonthlyBill = Math.max(5, bill - cappedMonthlySaving);
    const panels = 8 + (heatPump ? 4 : 0) + (ev ? 2 : 0);
    return {
      annualSaving,
      monthlySaving: cappedMonthlySaving,
      newMonthlyBill,
      annualKwh: annualKwhFromMonthlyBill(bill),
      panels,
      kwp: Math.round(panels * 0.48 * 100) / 100,
      battery: ev || heatPump ? 11.5 : 5,
      systemPrice: 9200 + (ev ? 1400 : 0) + (heatPump ? 1800 : 0),
      payback: (7.5 - bill / 300).toFixed(1),
    };
  }, [address, bill, ev, heatPump]);

  return (
    <div
      className={`flex flex-col gap-5 rounded-3xl border-2 border-ink bg-white p-6 shadow-hard sm:p-8 ${
        compact ? "" : "w-full max-w-2xl"
      }`}
    >
      {step === "postcode" && (
        <div className="flex flex-col gap-3">
          <label htmlFor="funnel-pc" className="text-sm font-bold text-muted">
            Let&apos;s find your roof
          </label>
          <PostcodeAddressLookup
            tone="powlo"
            submitLabel="Find my roof →"
            inputId="funnel-pc"
            selectMode="immediate"
            onAddressSelected={(fields) => {
              setAddress(fields);
              setStep("confirm");
              loadHomeFacts(fields);
            }}
          />
          <p className="m-0 text-xs leading-relaxed text-mutedLight">
            Enter your postcode and choose your address. Address search is live. The savings figures later in
            this flow are an illustrative estimate.
          </p>
        </div>
      )}

      {step === "confirm" && address && (
        <div className="flex flex-col gap-4">
          <div className="text-xs font-bold uppercase tracking-widest text-indigo">Good news — here&apos;s your starting point</div>
          <PropertyAerial address={composeAddress(address)} postcode={address.postcode} />
          <div className="rounded-2xl border border-border bg-cream p-5">
            <div className="font-display text-xl font-bold">{formatSelectedAddress(address)}</div>
            {checkingHome ? (
              <div className="mt-1 text-sm text-muted">Checking this home…</div>
            ) : startingPoint ? (
              <div className="mt-1 text-sm text-muted">{startingPoint}</div>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setStep("details")}
              className="min-h-[52px] flex-1 rounded-2xl bg-lime px-6 font-display font-bold text-ink"
            >
              Looks right, continue →
            </button>
            <button
              onClick={() => {
                cancelHomeFacts();
                setStep("postcode");
              }}
              className="min-h-[52px] rounded-2xl border-2 border-border px-6 font-semibold text-ink"
            >
              Try another postcode
            </button>
          </div>
        </div>
      )}

      {step === "details" && (
        <div className="flex flex-col gap-4">
          <div className="text-xs font-bold uppercase tracking-widest text-indigo">A few quick questions</div>
          <label className="flex flex-col gap-2 text-sm font-bold text-muted">
            Roughly what&apos;s your monthly electricity bill? ({gbp(bill)})
            <input
              type="range"
              min={40}
              max={400}
              step={10}
              value={bill}
              onChange={(e) => setBill(Number(e.target.value))}
              className="pslider"
              aria-label="Monthly electricity bill"
            />
          </label>
          <ToggleRow
            label="Electric car in the picture?"
            sub="Charged at home, about 6,000 miles a year"
            checked={ev}
            onChange={setEv}
          />
          <ToggleRow
            label="Air source heat pump?"
            sub="Or planning to install one — it changes what system makes sense"
            checked={heatPump}
            onChange={setHeatPump}
          />
          <button
            onClick={() => setStep("result")}
            className="min-h-[56px] rounded-2xl bg-lime px-6 font-display text-lg font-bold tracking-tight text-ink"
          >
            Get my full Energy Plan →
          </button>
        </div>
      )}

      {step === "result" && result && (
        <div className="flex flex-col gap-4">
          <div className="text-xs font-bold uppercase tracking-widest text-indigo">Solar &amp; battery package</div>
          <div className="rounded-2xl bg-navy p-6 text-white">
            <div className="text-sm text-[#C9D1DE]">Great news — you could be saving</div>
            <div className="font-display text-5xl font-bold tracking-tight text-lime">
              {gbp(result.annualSaving)}
            </div>
            <div className="mt-2 text-sm text-[#C9D1DE]">a year, every year, with a system sized to your home</div>
            <div className="mt-4 grid grid-cols-2 gap-3 border-t border-navyLight pt-4 text-sm text-[#C9D1DE]">
              <div>
                That&apos;s about
                <div className="font-display text-lg font-bold text-white">{gbp(result.monthlySaving)}/mo</div>
              </div>
              <div>
                Your bill, today {gbp(bill)}/mo, could drop to
                <div className="font-display text-lg font-bold text-lime">~{gbp(result.newMonthlyBill)}/mo</div>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              className="min-h-[52px] rounded-xl border-2 border-border px-4"
            />
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              placeholder="Where should we send it?"
              className="min-h-[52px] rounded-xl border-2 border-border px-4"
            />
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              type="tel"
              autoComplete="tel"
              placeholder="Phone number (optional)"
              className="min-h-[52px] rounded-xl border-2 border-border px-4"
            />
            <EmailPlanConsent
              checked={emailConsent}
              onCheckedChange={(next) => {
                setEmailConsent(next);
                setPreviewError(null);
              }}
            />
            {previewError && (
              <p className="m-0 text-sm font-semibold text-red-600">{previewError}</p>
            )}
            <button
              type="button"
              disabled={!emailConsent || !isValidEmail(email) || sendPreviewEmail.isPending || !address}
              onClick={() => {
                void (async () => {
                  if (!address || !result) return;
                  const customerName = name.trim();
                  const customerEmail = email.trim();
                  const customerPhone = phone.trim();
                  if (!emailConsent || !isValidEmail(customerEmail)) return;

                  setPreviewError(null);
                  try {
                    await sendPreviewEmail.mutateAsync({
                      emailConsent: true,
                      customerName,
                      customerEmail,
                      ...(customerPhone ? { customerPhone } : {}),
                      billData: {
                        annual_kwh: result.annualKwh,
                        current_tariff_pence_per_kwh: null,
                        energy_supplier: null,
                        annual_spend_gbp: bill * 12,
                        address: formatSelectedAddress(address),
                        postcode: address.postcode,
                        customer_name: customerName,
                        monthly_bill: bill,
                      },
                      includesEv: ev,
                      includesHeatPump: heatPump,
                      estimate: {
                        annualBenefit: result.annualSaving,
                        monthlySaving: result.monthlySaving,
                        panels: result.panels,
                        kwp: result.kwp,
                        batteryKwh: result.battery,
                        currentMonthlyBill: bill,
                      },
                    });
                  } catch (err) {
                    const fallback = "We couldn't send your Energy Plan email. Please try again.";
                    let message = fallback;
                    if (err instanceof ApiError) {
                      if (err.errors?.emailConsent?.[0]) {
                        message = err.errors.emailConsent[0];
                      } else if (err.errors?.customerEmail?.[0]) {
                        message = err.errors.customerEmail[0];
                      } else if (err.message && err.status !== 0) {
                        message = err.message;
                      }
                    }
                    setPreviewError(message);
                    toast.error(message);
                    return;
                  }

                  savePendingPlanDraft({
                    address,
                    customerName,
                    customerEmail,
                    customerPhone,
                    annualKwh: result.annualKwh,
                    includesEv: ev,
                    includesHeatPump: heatPump,
                  });
                  setPendingAddress(address);
                  if (customerName || customerEmail || customerPhone) {
                    setPendingPlanContact({ customerName, customerEmail, customerPhone });
                  }
                  try {
                    await session.persist("your_plan", {
                      includesEv: ev,
                      includesHeatPump: heatPump,
                      form: {
                        annualKwh: result.annualKwh,
                        address1: address.address1,
                        address2: address.address2,
                        city: address.city,
                        region: address.region,
                        address: composeAddress(address),
                        postcode: address.postcode,
                        customerName,
                        customerEmail,
                        customerPhone,
                        paymentType: "finance",
                      },
                    });
                  } catch {
                    // The plan still opens from the pending draft.
                  }
                  navigate("/plan?entry=address");
                })();
              }}
              className="min-h-[56px] rounded-2xl bg-lime px-6 font-display text-lg font-bold tracking-tight text-ink disabled:cursor-not-allowed disabled:opacity-50"
            >
              {sendPreviewEmail.isPending ? "Sending your plan…" : "See my full Energy Plan →"}
            </button>
            <p className="m-0 text-center text-xs text-mutedLight">
              We&apos;ll also email a copy to {email || "the address above"}
            </p>
          </div>
          <p className="m-0 text-xs leading-relaxed text-mutedLight">
            Demo estimate, not a live design. Your headline saving above is re-derived from your own answers, not
            from OS, EPC or Land Registry data. System price, payback and the full specification are on your
            Energy Plan — designed by AI from your roof, usage and live tariffs, then approved by a certified
            expert.
          </p>
        </div>
      )}
    </div>
  );
}
